import { fetchWithRetry as fetch, responseError } from './apiError';
import { modelCallRoutingService } from './modelCallRoutingService';
import { replayBarSettingsService } from './replayBarSettingsService';
import { skillStoreVisibilityAdminService } from './skillStoreVisibilityAdminService';
import { tokenService } from './tokenService';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export interface PricingInfo {
  price_per_credit: number;
  hourly_rate_senior: number;
  hourly_rate_lead: number;
  compute_cpu_credit_per_hour: number;
  compute_ram_credit_per_hour: number;
  compute_disk_credit_per_hour: number;
  chat_credit_free: number;
  chat_credit_plus: number;
  chat_credit_pro: number;
  chat_credit_byok: number;
  chat_credit_standard: number;
  section_credit_free: number;
  section_credit_plus: number;
  section_credit_pro: number;
  section_credit_byok: number;
  section_credit_standard: number;
  fix_credit_free: number;
  fix_credit_plus: number;
  fix_credit_pro: number;
  fix_credit_byok: number;
  fix_credit_standard: number;
  editor_credit_free: number;
  editor_credit_plus: number;
  editor_credit_pro: number;
  editor_credit_byok: number;
  editor_credit_standard: number;
  design_editor_revision_credit_free: number;
  design_editor_revision_credit_plus: number;
  design_editor_revision_credit_pro: number;
  design_editor_revision_credit_byok: number;
  design_editor_revision_credit_standard: number;
  document_credit_per_page: number;
  developer_mode_input_credit_per_million_tokens: number;
  developer_mode_output_credit_per_million_tokens: number;
  topup_price_free_usd: number;
  topup_price_byok_usd: number;
  topup_price_plus_usd: number;
  topup_price_pro_usd: number;
  topup_price_standard_usd: number;
  topup_price_free_inr: number;
  topup_price_byok_inr: number;
  topup_price_plus_inr: number;
  topup_price_pro_inr: number;
  topup_price_standard_inr: number;
  updated_at: string | null;
  updated_by: string | null;
}

export interface AdminProject {
  id: string;
  title: string;
  owner_id: string;
  owner_name: string;
  owner_email: string;
  created_at: string | null;
  is_active: boolean;
  show_on_explore: boolean;
  member_count: number;
  task_count: number;
  tasks_todo: number;
  tasks_error: number;
  tasks_ai_in_progress: number;
  tasks_done: number;
  total_prompt_credits: number;
  credit_price: number;
  total_cost: number;
}

interface PaginatedProjects {
  projects: AdminProject[];
  page: number;
  per_page: number;
  total: number;
  total_pages: number;
}

interface ProjectOwner {
  id: string | null;
  name: string;
  email: string;
}

interface ProjectMember {
  id: string;
  user_id: string;
  name: string;
  email: string;
  role: string;
}

export interface ProjectDetails {
  id: string;
  title: string;
  description: string | null;
  owner: ProjectOwner;
  created_at: string | null;
  updated_at: string | null;
  is_active: boolean;
  show_on_explore: boolean;
  members: ProjectMember[];
  task_summary: Record<string, number>;
  total_prompt_credits: number;
}

interface ProjectStats {
  project_id: string;
  total_tasks: number;
  total_prompt_credits: number;
  member_count: number;
  tasks_by_status: Record<string, number>;
}

export interface AdminUser {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  display_name: string | null;
  system_role: string;
  additional_roles: string[];
  is_active: boolean;
  is_suspicious: boolean;
  project_count: number;
  auth_methods: string[];
  subscription_tier: string;
  country: string | null;
  state_code: string | null;
  created_at: string | null;
  last_login: string | null;
  // Seriousness score
  seriousness_score: number | null;
  seriousness_label: string | null;
  scored_projects: number;
  latest_score_reason: string | null;
  // Satisfaction score
  satisfaction_score: number | null;
  satisfaction_label: string | null;
  satisfaction_scored_projects: number;
  latest_satisfaction_reason: string | null;
}

interface PaginatedUsers {
  users: AdminUser[];
  page: number;
  per_page: number;
  total: number;
  total_pages: number;
}

export interface UserDetails {
  id: string;
  email: string;
  display_name: string;
  first_name: string | null;
  last_name: string | null;
  system_role: string;
  additional_roles: string[];
  is_active: boolean;
  is_suspicious: boolean;
  email_verified: boolean;
  auth_provider: string;
  credits_balance: number;
  subscription_tier: string;
  country: string | null;
  state_code: string | null;
  created_at: string | null;
  updated_at: string | null;
  last_login: string | null;
}

export interface UserProject {
  id: string;
  title: string;
  role: string;
  is_active: boolean;
  created_at: string | null;
}

type FetchOptions = globalThis.RequestInit;
type FetchResponse = globalThis.Response;

async function fetchWithAuth(url: string, options?: FetchOptions): Promise<FetchResponse> {
  const token = tokenService.getAccessToken();

  return await fetch(url, {
    ...options,
    headers: { ...options?.headers, Authorization: `Bearer ${token}` },
  });
}

async function handleResponse<T>(response: FetchResponse, _operation: string): Promise<T> {
  if (!response.ok) throw await responseError(response);

  return response.json();
}

async function handleErrorResponse(response: FetchResponse, _operation: string): Promise<never> {
  throw await responseError(response);
}

function buildQueryParams(params: Record<string, string | number | string[] | undefined>): URLSearchParams {
  const query = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((entry) => query.append(key, entry));
    } else if (value !== undefined) {
      query.append(key, String(value));
    }
  });

  return query;
}

export interface ImpersonateResponse {
  access_token: string;
  refresh_token: string;
  user_id: string;
  display_name: string;
}

export interface AdminCoupon {
  id: string;
  code: string;
  credits: number;
  max_redemptions: number;
  redemption_count: number;
  is_active: boolean;
  signup_url: string;
  created_at: string;
  created_by: {
    id: string;
    email: string;
    display_name: string | null;
  };
}

export interface AdminCouponsPage {
  coupons: AdminCoupon[];
  page: number;
  per_page: number;
  total: number;
  total_pages: number;
}

type PricingUpdatePayload = {
  price_per_credit: number;
  hourly_rate_senior?: number;
  hourly_rate_lead?: number;
  compute_cpu_credit_per_hour?: number;
  compute_ram_credit_per_hour?: number;
  compute_disk_credit_per_hour?: number;
  chat_credit_free?: number;
  chat_credit_plus?: number;
  chat_credit_pro?: number;
  chat_credit_byok?: number;
  chat_credit_standard?: number;
  section_credit_free?: number;
  section_credit_plus?: number;
  section_credit_pro?: number;
  section_credit_byok?: number;
  section_credit_standard?: number;
  fix_credit_free?: number;
  fix_credit_plus?: number;
  fix_credit_pro?: number;
  fix_credit_byok?: number;
  fix_credit_standard?: number;
  editor_credit_free?: number;
  editor_credit_plus?: number;
  editor_credit_pro?: number;
  editor_credit_byok?: number;
  editor_credit_standard?: number;
  design_editor_revision_credit_free?: number;
  design_editor_revision_credit_plus?: number;
  design_editor_revision_credit_pro?: number;
  design_editor_revision_credit_byok?: number;
  design_editor_revision_credit_standard?: number;
  document_credit_per_page?: number;
  developer_mode_input_credit_per_million_tokens?: number;
  developer_mode_output_credit_per_million_tokens?: number;
  topup_price_free_usd?: number;
  topup_price_byok_usd?: number;
  topup_price_plus_usd?: number;
  topup_price_pro_usd?: number;
  topup_price_standard_usd?: number;
  topup_price_free_inr?: number;
  topup_price_byok_inr?: number;
  topup_price_plus_inr?: number;
  topup_price_pro_inr?: number;
  topup_price_standard_inr?: number;
};

export interface MonthlyInvoiceRow {
  invoice_number: string;
  invoice_date_ist: string;
  completed_at_utc: string;
  customer_name: string;
  email: string;
  organisation: string;
  provider: string;
  internal_payment_id: string;
  provider_payment_id: string;
  provider_order_id: string;
  description: string;
  invoice_type: string;
  credits: number;
  currency: string;
  country: string;
  state_code: string;
  state_name: string;
  gstin: string;
  amount: number;
  gst_rate: number;
  cgst: number;
  sgst: number;
  igst: number;
  gst_total: number;
  total: number;
  tax_review_flag: string;
}

export interface MonthlyInvoiceSummaryRow {
  currency: string;
  provider: string;
  invoice_count: number;
  taxable_amount: number;
  gst_total: number;
  total_amount: number;
}

export interface MonthlyInvoicesData {
  year: number;
  month: number;
  invoices: MonthlyInvoiceRow[];
  summary: MonthlyInvoiceSummaryRow[];
  page: number;
  per_page: number;
  total: number;
  total_pages: number;
}

export const adminService = {
  getCoupons: async (page: number = 1, perPage: number = 10): Promise<AdminCouponsPage> => {
    const params = buildQueryParams({ page, per_page: perPage });
    const response = await fetchWithAuth(`${API_URL}/api/v1/admin/coupons?${params}`);

    return handleResponse(response, 'Failed to fetch coupons');
  },

  createCoupon: async (
    credits: number,
    maxRedemptions: number,
    code?: string,
  ): Promise<AdminCoupon> => {
    const response = await fetchWithAuth(`${API_URL}/api/v1/admin/coupons`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        credits,
        max_redemptions: maxRedemptions,
        code: code || undefined,
      }),
    });

    return handleResponse(response, 'Failed to create coupon');
  },

  deleteCoupon: async (couponId: string): Promise<void> => {
    const response = await fetchWithAuth(`${API_URL}/api/v1/admin/coupons/${couponId}`, {
      method: 'DELETE',
    });

    if (!response.ok) throw await responseError(response);
  },

  getPricing: async (): Promise<PricingInfo> => {
    const response = await fetchWithAuth(`${API_URL}/api/v1/admin/settings/pricing`);

    return handleResponse(response, 'Failed to fetch pricing');
  },

  getMonthlyInvoices: async (
    year: number,
    month: number,
    page: number = 1,
    search?: string,
  ): Promise<MonthlyInvoicesData> => {
    const params = buildQueryParams({ year, month, page, per_page: 5, search });
    const response = await fetchWithAuth(`${API_URL}/api/v1/admin/finance/monthly-invoices?${params}`);

    return handleResponse(response, 'Failed to fetch monthly invoices');
  },

  downloadMonthlyInvoices: async (year: number, month: number): Promise<globalThis.Blob> => {
    const params = buildQueryParams({ year, month });
    const response = await fetchWithAuth(`${API_URL}/api/v1/admin/finance/monthly-invoices.xlsx?${params}`);

    if (!response.ok) return handleErrorResponse(response, 'Failed to download monthly invoices');

    return response.blob();
  },

  updatePricing: async (data: PricingUpdatePayload): Promise<PricingInfo> => {
    const response = await fetchWithAuth(`${API_URL}/api/v1/admin/settings/pricing`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });

    if (!response.ok) return handleErrorResponse(response, 'Failed to update pricing');

    return response.json();
  },

  getAllProjects: async (
    page: number = 1,
    perPage: number = 20,
    search?: string,
    statusFilter?: string,
    sortBy?: string,
    sortDirection?: string
  ): Promise<PaginatedProjects> => {
    const params = buildQueryParams({
      page, per_page: perPage, search, status_filter: statusFilter,
      sort_by: sortBy, sort_direction: sortDirection,
    });

    const response = await fetchWithAuth(`${API_URL}/api/v1/admin/projects?${params}`);

    return handleResponse(response, 'Failed to fetch projects');
  },

  getProjectDetails: async (projectId: string): Promise<ProjectDetails> => {
    const response = await fetchWithAuth(`${API_URL}/api/v1/admin/projects/${projectId}`);

    if (!response.ok) return handleErrorResponse(response, 'Failed to fetch project details');

    return response.json();
  },

  getProjectStats: async (projectId: string): Promise<ProjectStats> => {
    const response = await fetchWithAuth(`${API_URL}/api/v1/admin/projects/${projectId}/stats`);

    if (!response.ok) return handleErrorResponse(response, 'Failed to fetch project stats');

    return response.json();
  },

  deactivateProject: async (projectId: string): Promise<AdminProject> => {
    const response = await fetchWithAuth(`${API_URL}/api/v1/admin/projects/${projectId}/deactivate`, {
      method: 'PUT',
    });

    if (!response.ok) return handleErrorResponse(response, 'Failed to deactivate project');

    return response.json();
  },

  activateProject: async (projectId: string): Promise<AdminProject> => {
    const response = await fetchWithAuth(`${API_URL}/api/v1/admin/projects/${projectId}/activate`, {
      method: 'PUT',
    });

    if (!response.ok) return handleErrorResponse(response, 'Failed to activate project');

    return response.json();
  },

  updateProjectExploreVisibility: async (
    projectId: string,
    showOnExplore: boolean
  ): Promise<AdminProject> => {
    const response = await fetchWithAuth(`${API_URL}/api/v1/admin/projects/${projectId}/explore-visibility`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ show_on_explore: showOnExplore }),
    });

    if (!response.ok) return handleErrorResponse(response, 'Failed to update Explore visibility');

    return response.json();
  },

  getAllUsers: async (
    page: number = 1,
    perPage: number = 20,
    search?: string,
    roleFilter?: string,
    statusFilter?: string,
    sortBy?: string,
    sortDirection?: string,
    subscriptionFilter?: string[],
    qualityFilter?: string,
    satisfactionFilter?: string
  ): Promise<PaginatedUsers> => {
    const queryParams = {
      page, per_page: perPage, search, role_filter: roleFilter, status_filter: statusFilter,
      sort_by: sortBy, sort_direction: sortDirection, subscription_filter: subscriptionFilter,
      quality_filter: qualityFilter, satisfaction_filter: satisfactionFilter,
    };
    const params = buildQueryParams(queryParams);

    const response = await fetchWithAuth(`${API_URL}/api/v1/admin/users?${params}`);

    return handleResponse(response, 'Failed to fetch users');
  },

  getUserDetails: async (userId: string): Promise<UserDetails> => {
    const response = await fetchWithAuth(`${API_URL}/api/v1/admin/users/${userId}`);

    if (!response.ok) return handleErrorResponse(response, 'Failed to fetch user details');

    return response.json();
  },

  getUserProjects: async (userId: string): Promise<UserProject[]> => {
    const response = await fetchWithAuth(`${API_URL}/api/v1/admin/users/${userId}/projects`);

    if (!response.ok) return handleErrorResponse(response, 'Failed to fetch user projects');

    return response.json();
  },

  updateUserRole: async (userId: string, role: string): Promise<UserDetails> => {
    const response = await fetchWithAuth(`${API_URL}/api/v1/admin/users/${userId}/role`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ role }),
    });

    if (!response.ok) return handleErrorResponse(response, 'Failed to update user role');

    return response.json();
  },

  updateUserAdditionalRoles: async (userId: string, roles: string[]): Promise<UserDetails> => {
    const response = await fetchWithAuth(`${API_URL}/api/v1/admin/users/${userId}/additional-roles`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ roles }),
    });

    if (!response.ok) return handleErrorResponse(response, 'Failed to update user additional roles');

    return response.json();
  },

  updateSubscriptionTier: async (userId: string, tier: string): Promise<UserDetails> => {
    const response = await fetchWithAuth(`${API_URL}/api/v1/admin/users/${userId}/subscription`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tier }),
    });

    if (!response.ok) return handleErrorResponse(response, 'Failed to update subscription tier');

    return response.json();
  },

  deactivateUser: async (userId: string): Promise<AdminUser> => {
    const response = await fetchWithAuth(`${API_URL}/api/v1/admin/users/${userId}/deactivate`, {
      method: 'PUT',
    });

    if (!response.ok) return handleErrorResponse(response, 'Failed to deactivate user');

    return response.json();
  },

  activateUser: async (userId: string): Promise<AdminUser> => {
    const response = await fetchWithAuth(`${API_URL}/api/v1/admin/users/${userId}/activate`, {
      method: 'PUT',
    });

    if (!response.ok) return handleErrorResponse(response, 'Failed to activate user');

    return response.json();
  },

  updateSuspiciousStatus: async (userId: string, isSuspicious: boolean): Promise<UserDetails> => {
    const response = await fetchWithAuth(`${API_URL}/api/v1/admin/users/${userId}/suspicious`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ is_suspicious: isSuspicious }),
    });

    if (!response.ok) return handleErrorResponse(response, 'Failed to update referral risk');

    return response.json();
  },

  addCredits: async (userId: string, amount: number, description?: string): Promise<{
    user_id: string; amount: number; new_balance: number; description: string;
  }> => {
    const body: { amount: number; description?: string } = { amount };

    if (description) body.description = description;

    const response = await fetchWithAuth(`${API_URL}/api/v1/admin/users/${userId}/credits`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

    if (!response.ok) return handleErrorResponse(response, 'Failed to add credits');

    return response.json();
  },

  loginAsUser: async (userId: string): Promise<ImpersonateResponse> => {
    const response = await fetchWithAuth(`${API_URL}/api/v1/admin/users/${userId}/impersonate`, {
      method: 'POST',
    });

    if (!response.ok) return handleErrorResponse(response, 'Failed to impersonate user');

    return response.json();
  },

  // System OAuth Keys
  getSystemOAuthKeys: async () => {
    const response = await fetchWithAuth(`${API_URL}/api/v1/admin/system-oauth-keys`);

    return handleResponse(response, 'Failed to fetch system OAuth keys');
  },

  addSystemOAuthKey: async (label: string, token: string) => {
    const response = await fetchWithAuth(`${API_URL}/api/v1/admin/system-oauth-keys`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ label, token }),
    });

    if (!response.ok) return handleErrorResponse(response, 'Failed to add key');

    return response.json();
  },

  deleteSystemOAuthKey: async (keyIndex: number) => {
    const response = await fetchWithAuth(
      `${API_URL}/api/v1/admin/system-oauth-keys/${keyIndex}`,
      { method: 'DELETE' },
    );

    if (!response.ok) return handleErrorResponse(response, 'Failed to delete key');

    return response.json();
  },

  testSystemOAuthKey: async (keyIndex: number): Promise<{ status: string; message: string }> => {
    const response = await fetchWithAuth(
      `${API_URL}/api/v1/admin/system-oauth-keys/${keyIndex}/test`,
      { method: 'POST' },
    );

    if (!response.ok) return handleErrorResponse(response, 'Failed to test key');

    return response.json();
  },

  toggleSystemOAuthKey: async (
    keyIndex: number, enabled: boolean,
  ): Promise<{ enabled: boolean }> => {
    const response = await fetchWithAuth(
      `${API_URL}/api/v1/admin/system-oauth-keys/${keyIndex}/toggle`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled }),
      },
    );

    if (!response.ok) return handleErrorResponse(response, 'Failed to toggle key');

    return response.json();
  },

  // System MiMo API Keys
  getSystemMimoApiKeys: async () => {
    const response = await fetchWithAuth(`${API_URL}/api/v1/admin/system-mimo-api-keys`);

    return handleResponse(response, 'Failed to fetch system MiMo API keys');
  },

  addSystemMimoApiKey: async (label: string, token: string, apiBase?: string) => {
    const response = await fetchWithAuth(`${API_URL}/api/v1/admin/system-mimo-api-keys`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ label, token, api_base: apiBase }),
    });

    if (!response.ok) return handleErrorResponse(response, 'Failed to add MiMo API key');

    return response.json();
  },

  deleteSystemMimoApiKey: async (keyIndex: number) => {
    const response = await fetchWithAuth(
      `${API_URL}/api/v1/admin/system-mimo-api-keys/${keyIndex}`,
      { method: 'DELETE' },
    );

    if (!response.ok) return handleErrorResponse(response, 'Failed to delete MiMo API key');

    return response.json();
  },

  testSystemMimoApiKey: async (keyIndex: number): Promise<{ status: string; message: string }> => {
    const response = await fetchWithAuth(
      `${API_URL}/api/v1/admin/system-mimo-api-keys/${keyIndex}/test`,
      { method: 'POST' },
    );

    if (!response.ok) return handleErrorResponse(response, 'Failed to test MiMo API key');

    return response.json();
  },

  toggleSystemMimoApiKey: async (
    keyIndex: number, enabled: boolean,
  ): Promise<{ enabled: boolean }> => {
    const response = await fetchWithAuth(
      `${API_URL}/api/v1/admin/system-mimo-api-keys/${keyIndex}/toggle`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled }),
      },
    );

    if (!response.ok) return handleErrorResponse(response, 'Failed to toggle MiMo API key');

    return response.json();
  },

  // Agent OAuth Toggle
  getAgentOAuthToggle: async (): Promise<{ enabled: boolean }> => {
    const response = await fetchWithAuth(`${API_URL}/api/v1/admin/settings/agent-oauth-toggle`);

    return handleResponse(response, 'Failed to fetch agent OAuth toggle');
  },

  setAgentOAuthToggle: async (enabled: boolean): Promise<{ enabled: boolean }> => {
    const response = await fetchWithAuth(`${API_URL}/api/v1/admin/settings/agent-oauth-toggle`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ enabled }),
    });

    if (!response.ok) return handleErrorResponse(response, 'Failed to update agent OAuth toggle');

    return response.json();
  },

  // System-admin AI Overview replay bar
  ...replayBarSettingsService,

  // Per-call-type LLM routing lane (probe/fallback | direct API | OAuth)
  ...modelCallRoutingService,

  // Skill Store surfaces (store link, skill builder, cashout)
  ...skillStoreVisibilityAdminService,

  // User Events
  getUserEvents: async (
    userId: string, cursor?: string, limit: number = 100,
  ): Promise<UserEventsResponse> => {
    const params = new URLSearchParams({ limit: String(limit) });

    if (cursor) params.set('cursor', cursor);

    const response = await fetchWithAuth(
      `${API_URL}/api/v1/admin/users/${userId}/events?${params}`,
    );

    return handleResponse(response, 'Failed to fetch user events');
  },

  // Visitor Tracking Data
  getUserVisitorData: async (
    userId: string, page: number = 1, perPage: number = 50,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ): Promise<any> => {
    const params = new URLSearchParams({ page: String(page), per_page: String(perPage) });

    const response = await fetchWithAuth(
      `${API_URL}/api/v1/admin/users/${userId}/visitor-data?${params}`,
    );

    return handleResponse(response, 'Failed to fetch visitor data');
  },

  getProjectIntents: async (projectId: string, page = 1, perPage = 10): Promise<ProjectIntentsResponse> => {
    const response = await fetchWithAuth(
      `${API_URL}/api/v1/admin/projects/${projectId}/intents?page=${page}&per_page=${perPage}`,
    );

    return handleResponse(response, 'Failed to fetch project intents');
  },
};

export interface UserEvent {
  id: string;
  user_id: string;
  event_type: string;
  url: string;
  button_id: string | null;
  button_text: string | null;
  button_parent_class: string | null;
  form_data: string | null;
  ga_event_name: string | null;
  ga_params: string | null;
  timestamp: string;
  created_at: string;
}

export interface UserEventsResponse {
  events: UserEvent[];
  next_cursor: string | null;
  has_more: boolean;
}

export interface ProjectIntent {
  id: string;
  project_id: string;
  intent: string;
  category: string;
  other_detail: string | null;
  confidence: number | null;
  source_message_count: number;
  created_at: string;
}

export interface ProjectIntentsResponse {
  project_id: string;
  intents: ProjectIntent[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}
